import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';

import { IconAlertTriangle } from '@tabler/icons-react-native';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { useLogoutRoutine } from 'app/hooks/use-logout-routine';
import { Event, track } from 'app/lib/analytics';
import { StorageKey, storage } from 'app/lib/storage';
import { useMyProfile } from 'app/network/profile';
import { useDeleteUser } from 'app/network/user';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AccountDeletionScreenComponent: NavigationScreen<'account-deletion'> = ({ navigation }) => {
  const profileQuery = useMyProfile();
  const { t } = useScopedTranslation();
  const logoutRoutine = useLogoutRoutine();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const setPageTitle = usePageTitle('account-deletion');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  const deleteUser = useDeleteUser({
    async onSuccess() {
      const currentUserId = await storage.readString(StorageKey.CurrentUserId);
      track(Event.UserInitiatedLogout, { source: 'account_deletion', userId: currentUserId ?? 'null' });
      await logoutRoutine();
      navigation.reset({ routes: [{ name: 'main' }] });
    },
  });

  return (
    <>
      <Page title={t('title')}>
        {!profileQuery.data ? (
          <Thumbnail.Skeleton />
        ) : (
          <Thumbnail alignSelf="center" source={{ uri: profileQuery.data.picture }} />
        )}

        <Row alignItems="flex-start" flex={1} marginTop="spacing.general.x8" minWidth={0}>
          <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} />

          <Column flex={1} flexShrink={1} marginStart="spacing.general.x2" minWidth={0}>
            <Text flexShrink={1} fontWeight="700" variant="bodyBase">
              {t('disclaimer')}
            </Text>

            <Text flexShrink={1} marginTop="spacing.general.x1">
              {t('subtitle')}
            </Text>
          </Column>
        </Row>
      </Page>

      <Accessory>
        {deleteUser.error ? (
          <Row alignSelf="stretch" marginBottom="spacing.general.x4" marginHorizontal="spacing.general.x4" minWidth={0}>
            <Column flex={1} minWidth={0}>
              <RemoteError {...{ error: deleteUser.error, message: deleteUser.error.message }} />
            </Column>
          </Row>
        ) : null}

        <Button
          loading={deleteUser.isPending}
          marginHorizontal="spacing.general.x4"
          onPress={() => setIsConfirmationOpen(true)}
          variant="primary"
        >
          {t('primaryButtonText')}
        </Button>
      </Accessory>
      <DeleteAccountConfirmationModal
        isLoading={deleteUser.isPending}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={() => deleteUser.mutate()}
        open={isConfirmationOpen}
      />
    </>
  );
};

const AccountDeletionScreenWithErrorBoundary = withErrorBoundary(AccountDeletionScreenComponent);

export const AccountDeletionScreen: NavigationScreen<'account-deletion'> = (props) => (
  <AccountDeletionScreenWithErrorBoundary {...props} />
);

interface DeleteAccountButtonProps {
  content?: React.ReactNode;
}

interface DeleteAccountConfirmationModalProps {
  content?: React.ReactNode;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
}

function DeleteAccountConfirmationModal({
  content,
  isLoading,
  onClose,
  onConfirm,
  open,
}: DeleteAccountConfirmationModalProps) {
  const theme = useTheme();
  const { t } = useTranslation('account-deletion');

  return (
    <Modal onClose={onClose} open={open} transparent>
      <Modal.Card>
        <Modal.Title allowClose onClose={onClose}>
          {t('title')}
        </Modal.Title>

        {content}

        <Row
          backgroundColor="surface.tertiary-neutral"
          padding="spacing.general.x4"
          gap="spacing.general.x4"
          borderRadius="radius.lg"
        >
          <Column>
            <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} />
          </Column>
          <Column flex={1} gap="spacing.general.x2">
            <Text fontWeight="600" variant="bodyBase" color="text.secondary">
              {t('disclaimer')}
            </Text>
            <Text variant="bodyBase">{t('subtitle')}</Text>
          </Column>
        </Row>
        <Row>
          <Button flex={1} onPress={onClose} variant="secondary">
            {t('backButtonText')}
          </Button>
          <Button
            flex={1}
            loading={isLoading}
            marginStart="spacing.general.x2"
            onPress={onConfirm}
            style={{ backgroundColor: theme.colors['surface.error.default'] }}
          >
            <Text color="text.inverse.primary">{t('primaryButtonText')}</Text>
          </Button>
        </Row>
      </Modal.Card>
    </Modal>
  );
}

export const DeleteAccountButton: React.FC<DeleteAccountButtonProps> = ({ content }) => {
  const navigation = useNavigation<'account-deletion'>();

  const [isOpen, setIsOpen] = React.useState(false);

  const { t } = useTranslation('account-deletion');
  const logoutRoutine = useLogoutRoutine();

  const setPageTitle = usePageTitle('account-deletion');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { isLargeUI } = useUISize();

  const deleteUser = useDeleteUser({
    async onSuccess() {
      const currentUserId = await storage.readString(StorageKey.CurrentUserId);
      track(Event.UserInitiatedLogout, { source: 'account_deletion', userId: currentUserId ?? 'null' });
      await logoutRoutine();
      navigation.reset({ routes: [{ name: isLargeUI ? 'onboardingIntro' : 'presentPartageClub' }] });
    },
  });

  return (
    <>
      <Button
        loading={deleteUser.isPending}
        onPress={() => setIsOpen(true)}
        paddingHorizontal="spacing.general.x4"
        variant="primary"
      >
        {t('primaryButtonText')}
      </Button>
      <DeleteAccountConfirmationModal
        content={content}
        isLoading={deleteUser.isPending}
        onClose={() => setIsOpen(false)}
        onConfirm={() => deleteUser.mutate()}
        open={isOpen}
      />
    </>
  );
};
